import React, { useState, useEffect, useCallback } from "react";
import { Sparkles, ShoppingCart, Plus, Check, Zap, Camera, Gift, PartyPopper, Flame, Palette, Type, Cloud, Cake, Image, Aperture, Gem, DoorOpen, Circle, Cookie, Flower } from "lucide-react";
import { API_URL } from "../../constants";

const ICON_MAP = {
  sparkles: Sparkles,
  type: Type,
  circle: Circle,
  palette: Palette,
  cloud: Cloud,
  "door-open": DoorOpen,
  cake: Cake,
  image: Image,
  cookie: Cookie,
  gem: Gem,
  camera: Camera,
  aperture: Aperture,
  gift: Gift,
  "party-popper": PartyPopper,
  flame: Flame,
  zap: Zap,
  flower: Flower,
};

const CATEGORY_COLORS = {
  Decor: { bg: "bg-[#E83E8C]/10", text: "text-[#E83E8C]", border: "border-[#E83E8C]/20" },
  Cake: { bg: "bg-[#FF9F1C]/10", text: "text-[#FF9F1C]", border: "border-[#FF9F1C]/20" },
  Experience: { bg: "bg-[#7C3AED]/10", text: "text-[#7C3AED]", border: "border-[#7C3AED]/20" },
};

const UpsellCard = ({ item, isAdded, onToggle }) => {
  const Icon = ICON_MAP[item.icon] || Sparkles;
  const colors = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.Decor;

  return (
    <div
      className={`relative bg-white rounded-2xl border-2 overflow-hidden transition-all duration-300 hover:shadow-lg ${
        isAdded ? "border-[#25D366] shadow-md ring-1 ring-[#25D366]/20" : "border-gray-100 hover:border-gray-200"
      }`}
      data-testid={`upsell-card-${item.id}`}
    >
      {/* Category tag */}
      <div className="px-5 pt-4 pb-0">
        <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${colors.bg} ${colors.text}`}>
          {item.category}
        </span>
      </div>

      <div className="p-5 pt-3">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${colors.bg}`}>
            <Icon size={20} className={colors.text} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-[#1E1B4B] text-sm leading-tight">{item.name}</h4>
            <p className="text-xl font-bold text-[#1E1B4B] mt-1">
              Rs {item.price.toLocaleString()}
            </p>
          </div>
        </div>

        <p className="text-sm text-[#475569] mt-3 leading-relaxed">{item.benefit}</p>

        <div className={`mt-3 px-3 py-2 rounded-lg ${colors.bg} ${colors.border} border`}>
          <p className="text-xs ${colors.text} italic">"{item.why_fits}"</p>
        </div>

        <button
          onClick={() => onToggle(item.id)}
          className={`mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-full font-semibold text-sm transition-all ${
            isAdded
              ? "bg-[#25D366] text-white hover:bg-[#1da851]"
              : "bg-[#1E1B4B] text-white hover:bg-[#2d2860]"
          }`}
          data-testid={`upsell-add-${item.id}`}
        >
          {isAdded ? <><Check size={16} /> Added</> : <><Plus size={16} /> Add to My Party</>}
        </button>
      </div>
    </div>
  );
};

export const AIUpsellEngine = ({ personality, budget, occasion, vibe, dark = false }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addedItems, setAddedItems] = useState(new Set());
  const [error, setError] = useState(null);

  const fetchSuggestions = useCallback(async () => {
    if (!personality) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/party-dna/upsell`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          personality_name: personality.personality_name,
          personality_description: personality.short_description,
          budget: budget || "moderate",
          occasion: occasion || "Birthday",
          vibe: vibe || "fun",
        }),
      });
      if (!res.ok) throw new Error("Failed to load suggestions");
      const data = await res.json();
      if (data.success) setSuggestions(data.suggestions);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [personality, budget, occasion, vibe]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const handleToggle = useCallback((id) => {
    setAddedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const totalAdded = suggestions.filter((s) => addedItems.has(s.id));
  const totalPrice = totalAdded.reduce((sum, s) => sum + s.price, 0);

  if (!personality) return null;

  return (
    <div className="mt-8" data-testid="ai-upsell-section">
      {/* Header */}
      <div className="text-center mb-6">
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-3 ${dark ? "bg-white/5 border-[#C084FC]/30" : "bg-gradient-to-r from-[#E83E8C]/10 to-[#FF9F1C]/10 border-[#E83E8C]/20"}`}>
          <Zap size={16} className="text-[#FF9F1C]" />
          <span className={`text-sm font-semibold ${dark ? "text-white" : "text-[#1E1B4B]"}`}>AI-Powered Picks</span>
        </div>
        <h3 className={`text-2xl md:text-3xl font-bold ${dark ? "text-white" : "text-[#1E1B4B]"}`}>
          People like you added...
        </h3>
        <p className={`text-sm mt-1 ${dark ? "text-white/75" : "text-[#475569]"}`}>
          Handpicked for <span className={`font-semibold ${dark ? "text-[#F0ABFC]" : "text-[#E83E8C]"}`}>{personality.personality_name}</span> personalities
        </p>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12" data-testid="upsell-loading">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 border-3 border-[#E83E8C]/30 border-t-[#E83E8C] rounded-full animate-spin" />
            <span className={`text-sm ${dark ? "text-white/75" : "text-[#475569]"}`}>Finding perfect add-ons for you...</span>
          </div>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="text-center py-8">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button
            onClick={fetchSuggestions}
            className="px-4 py-2 rounded-full text-sm font-semibold text-[#E83E8C] border border-[#E83E8C] hover:bg-[#E83E8C]/5"
            data-testid="upsell-retry"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Suggestion Cards */}
      {!loading && suggestions.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {suggestions.map((item) => (
              <UpsellCard
                key={item.id}
                item={item}
                isAdded={addedItems.has(item.id)}
                onToggle={handleToggle}
              />
            ))}
          </div>

          {/* Cart summary */}
          {totalAdded.length > 0 && (
            <div className="mt-6 bg-gradient-to-r from-[#1E1B4B] to-[#2d2860] rounded-2xl p-5 flex items-center justify-between flex-wrap gap-4" data-testid="upsell-cart-summary">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <ShoppingCart size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">
                    {totalAdded.length} add-on{totalAdded.length > 1 ? "s" : ""} selected
                  </p>
                  <p className="text-white/60 text-xs">
                    {totalAdded.map((s) => s.name).join(" + ")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-2xl font-bold text-white">Rs {totalPrice.toLocaleString()}</span>
                <a
                  href="#book-online"
                  className="flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white hover:opacity-90 transition-all hover:scale-105 shadow-lg"
                  data-testid="upsell-book-now"
                >
                  <Sparkles size={16} /> Book with Add-ons
                </a>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
